import moment from 'moment';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import {
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    DropdownFilterContext,
    ReactiveComputedFieldContext,
} from '@fitogest/reactive-form-builder/interfaces';
import type { FincasModel } from '../../../../../../models/form-common/fincas.module';
import type { SectorModel } from '../../../../../../models/form-common/sector.model';
import type { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';
import type { TareasBioModel } from '../../../../../../models/tareas/tareas-bio.model';
import { TareasBioEnvironmentDefinition } from '../../environment-variables';
import { TareasBioListsDefinition } from './list-variables';

export class TareasBioFormDefinition {
    public env: TareasBioEnvironmentDefinition = new TareasBioEnvironmentDefinition();
    public lists: TareasBioListsDefinition = new TareasBioListsDefinition();
    public readonly sectoresMultiple = this.env.showTareasBioSectorMultiple;
    public readonly parcelasMultiple = this.env.showTareasBioParcelaMultiple;

    public formType!: FormType;
    public model: Partial<TareasBioModel> = {};
    public formFields!: Form<TareasBioModel>;
    public fieldNames: FormField<TareasBioModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<TareasBioModel>) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'bio',
            header: 'Datos del bio',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    public calculateHorasTotales(): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.model.hora_inicio),
            this.normalizeTimeValue(this.model.hora_fin),
        );
    }

    private getFormSections(): FormSection<TareasBioModel>[] {
        const sections: FormSection<TareasBioModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getLocationFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getDateAndWorkerFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getBioFields(),
                order: 3,
            },
            {
                name: '',
                width: 12,
                isVisible: this.env.appName === 'hernandorena',
                fields: this.getHernandorenaFields(),
                order: 4,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getStatusFields(),
                order: 5,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 6,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLocationFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: this.formType < FormType.DELETION ? 'id_cliente' : 'cliente_nombre',
                vinculatedFields: undefined,
                label: this.env.clienteName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.clientes,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: true,
                    isVisible: this.shouldShowClientSelector(),
                }),
                order: 1,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                dependencies: [
                    {
                        source: 'id_cliente',
                        target: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterFincaByClient(it.value as FincasModel | null, context),
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 2,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.env.sectorName,
                input: {
                    type: this.sectoresMultiple ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN,
                    data: this.lists.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterSectorByFinca(it.value as SectorModel | null, context),
                    },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName !== 'hernandorena' }),
                order: 3,
            },
            {
                name: this.formType < FormType.DELETION ? 'ids_parcelas' : 'parcela',
                dependencies: [
                    {
                        source: 'id_cliente',
                        target: this.formType < FormType.DELETION ? 'ids_parcelas' : 'parcela',
                        kind: 'filter'
                    },
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'ids_parcelas' : 'parcela',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: this.formType < FormType.DELETION ? 'ids_parcelas' : 'parcela',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.env.parcelasName,
                input: {
                    type: this.parcelasMultiple ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN_SEARCH,
                    data: this.lists.parcelas,
                    isEditable: true,
                    style: 'min-height: 29px !important;',
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterParcela(it.value as ParcelasModel | null, context),
                    },
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: true,
                    isVisible: this.env.showTareasParcela,
                }),
                order: 4,
            },
        ]);
    }

    private getDateAndWorkerFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'fecha_receta',
                vinculatedFields: undefined,
                label: this.env.appName === 'hernandorena' ? 'Fecha' : 'Fecha Orden',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: this.env.appName === 'agrotata' }),
                order: 1,
            },
            {
                name: 'fecha_aplicacion',
                vinculatedFields: undefined,
                label: 'Fecha de aplicación',
                input: {
                    type: InputType.CALENDAR,
                    ...this.getDefaultApplicationDateInput(),
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: this.env.appName === 'agrotata',
                    isVisible: this.env.appName !== 'hernandorena',
                }),
                order: 2,
            },
            {
                name: 'hora_inicio',
                vinculatedFields: undefined,
                label: 'Hora Inicio',
                input: { type: InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({
                    isRequired: this.env.appName === 'agrotata',
                    isVisible: this.env.timeStartStopTareas && this.env.appName !== 'hernandorena',
                }),
                order: 3,
            },
            {
                name: 'hora_fin',
                vinculatedFields: undefined,
                label: 'Hora Fin',
                input: {
                    type: InputType.TIME,
                    ...this.getDefaultEndTimeInput(),
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: this.env.appName === 'agrotata',
                    isVisible: this.env.timeStartStopTareas && this.env.appName !== 'hernandorena',
                }),
                order: 4,
            },
            {
                name: 'horas_totales',
                vinculatedFields: ['hora_inicio', 'hora_fin'],
                label: 'Horas',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                computed: {
                    dependsOn: ['hora_inicio', 'hora_fin'],
                    resolve: (context) => this.formatHoursMinutes(
                        this.normalizeTimeValue(context.getValue('hora_inicio')),
                        this.normalizeTimeValue(context.getValue('hora_fin')),
                    ),
                },
                updateValue: () => this.calculateHorasTotales(),
                conditions: this.conditions({
                    isVisible: this.env.tareasHorasTotales && this.env.appName !== 'hernandorena',
                }),
                order: 5,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_trabajadores' : 'trabajadores_nombre',
                vinculatedFields: undefined,
                label: 'Trabajadores',
                input: {
                    type: InputType.MULTISELECT_SEARCH,
                    data: this.lists.trabajadores,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName !== 'hernandorena' }),
                order: 6,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_maquinaria' : 'maquinaria',
                vinculatedFields: undefined,
                label: 'Maquinaria',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.maquinarias,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'agrotata' }),
                order: 7,
            },
        ]);
    }

    private getBioFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'producto',
                vinculatedFields: undefined,
                label: 'Producto',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.productos,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'tipo_plaga',
                vinculatedFields: undefined,
                label: 'Tipo de plaga',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.tipoPlaga,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'dosis',
                vinculatedFields: undefined,
                label: 'Dosis',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'modo_accion',
                vinculatedFields: undefined,
                label: this.env.appName === 'hernandorena' ? 'Tipo medida' : 'Modo de acción',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'forma_aplicacion',
                vinculatedFields: undefined,
                label: 'Forma de aplicación (intensidad)',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'suelta_m2',
                vinculatedFields: undefined,
                label: 'Suelta m²',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName !== 'hernandorena' }),
                order: 6,
            },
            {
                name: 'motivo',
                vinculatedFields: undefined,
                label: 'Motivo',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 6,
                conditions: this.conditions({ isVisible: this.env.showBioMotivo }),
                order: 7,
            },
        ]);
    }

    private getHernandorenaFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'ph_caldo',
                vinculatedFields: undefined,
                label: 'pH Caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 1,
            },
            {
                name: 'ph_agua',
                vinculatedFields: undefined,
                label: 'pH Agua',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 2,
            },
            {
                name: 'litros_caldo',
                vinculatedFields: undefined,
                label: 'litros de caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 3,
            },
            {
                name: 'm2',
                vinculatedFields: undefined,
                label: 'm²',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 4,
            },
            {
                name: 'cantidad_total_producto',
                vinculatedFields: undefined,
                label: 'Cantidad total producto',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'hernandorena' }),
                order: 5,
            },
        ]);
    }

    private getStatusFields(): FormField<TareasBioModel>[] {
        return this.registerFields([
            {
                name: 'facturado',
                vinculatedFields: undefined,
                label: 'Facturado',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'agrosalvi' }),
                order: 1,
            },
            {
                name: 'factura_albaran',
                vinculatedFields: undefined,
                label: 'Factura/Albarán',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'agrosalvi' }),
                order: 2,
            },
            {
                name: 'orden',
                vinculatedFields: undefined,
                label: this.env.appName === 'hernandorena' ? 'Estado' : 'Orden',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.estados,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.showBioOrden }),
                order: 3,
            },
            {
                name: 'estado_tarea',
                vinculatedFields: undefined,
                label: 'Estado de la Tarea',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.estadoTarea,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'onda' || this.env.appName === 'lacooperativa' }),
                order: 4,
            },
            {
                name: 'empresa_pagadora',
                vinculatedFields: undefined,
                label: 'Empresa Pagadora',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'onda' || this.env.appName === 'lacooperativa' }),
                order: 5,
            },
        ]);
    }

    private getHiddenFields(): FormField<TareasBioModel>[] {
        return [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions({ isRememberedOnDuplicate: false }),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveClientId(),
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['id_finca'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) => this.resolveClientId(context.getValue('id_finca')),
                },
                conditions: this.conditions({ isVisible: !this.shouldShowClientSelector() }),
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['id_finca'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) => this.resolveUserId(context.getValue('id_finca')),
                },
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'cultivo',
                vinculatedFields: ['ids_parcelas'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['ids_parcelas'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) => 
                        this.getSelectedParcelasValues('cultivo', context.getValue('ids_parcelas')),
                },
                conditions: this.conditions({ isVisible: this.env.showTareasParcela }),
                order: 4,
            },
            {
                name: 'variedad',
                vinculatedFields: ['ids_parcelas'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                computed: {
                    dependsOn: ['ids_parcelas'],
                    resolve: (context: ReactiveComputedFieldContext<TareasBioModel>) => 
                            this.getSelectedParcelasValues('variedad', context.getValue('ids_parcelas')),
                },
                conditions: this.conditions({ isVisible: this.env.showTareasParcela }),
                order: 5,
            },
        ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private registerFields(fields: FormField<TareasBioModel>[]): FormField<TareasBioModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private filterFincaByClient(finca: FincasModel | null, context?: DropdownFilterContext<TareasBioModel>): boolean {
        if (finca === null) {
            return true;
        }

        const currentFincaId = this.getEntityId(this.getContextValue(context, 'id_finca', this.model.id_finca));

        if (currentFincaId.length > 0 && String(finca.id ?? '') === currentFincaId) {
            return true;
        }

        const clientId = this.getSelectedClientId(context);

        if (clientId.length === 0) {
            return true;
        }

        return String(finca.id_cliente ?? '') === clientId;
    }

    private filterSectorByFinca(sector: SectorModel | null, context?: DropdownFilterContext<TareasBioModel>): boolean {
        if (sector === null) {
            return true;
        }

        const fincaId = this.getEntityId(this.getContextValue(context, 'id_finca', this.model.id_finca));

        return fincaId.length > 0 && String(sector.id_finca ?? '') === fincaId;
    }

    private filterParcela(parcela: ParcelasModel | null, context?: DropdownFilterContext<TareasBioModel>): boolean {
        if (parcela === null) {
            return true;
        }

        if (parcela.activo?.toString() !== '1') {
            return false;
        }

        const selectedSectorIds = this.getSelectedSectorIds(context);

        if (selectedSectorIds.length > 0) {
            return selectedSectorIds.includes(String(parcela.id_sector ?? ''));
        }

        const fincaId = this.getEntityId(this.getContextValue(context, 'id_finca', this.model.id_finca));

        if (fincaId.length > 0) {
            return String(parcela.id_finca ?? '') === fincaId;
        }

        const clientId = this.getSelectedClientId(context);

        if (clientId.length > 0) {
            return String(parcela.id_cliente ?? '') === clientId;
        }

        return false;
    }

    private getSelectedSectorIds(context?: DropdownFilterContext<TareasBioModel>): string[] {
        return this.getReactiveValueTokens(this.getContextValue(context, 'id_sector', this.model.id_sector));
    }

    private getSelectedClientId(context?: DropdownFilterContext<TareasBioModel>): string {
        const clientValue = this.getContextValue(context, 'id_cliente', this.model.id_cliente ?? this.client);

        return this.getEntityId(clientValue);
    }

    private getContextValue(
        context: DropdownFilterContext<TareasBioModel> | undefined,
        fieldName: keyof TareasBioModel,
        fallback: unknown,
    ): unknown {
        return context?.model.get(String(fieldName))?.value ?? fallback;
    }

    private getEntityId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '').trim();
        }

        return String(value ?? '').trim();
    }

    private getSelectedParcelasValues(key: 'cultivo' | 'variedad', selectedValue: unknown = this.model.ids_parcelas): string {
        return this.resolveSelectedDropdownEntities(this.lists.parcelas, selectedValue)
            .map((parcela) => String(parcela?.[key] ?? '').trim())
            .filter((value) => value.length > 0)
            .join(';');
    }

    private resolveClientId(selectedFincaValue: unknown = this.model.id_finca): string {
        const selectedFinca = this.resolveSelectedDropdownEntity(this.lists.fincas, selectedFincaValue);
        const fincaClientId = selectedFinca?.id_cliente;
        const modelClientId = this.getEntityId(this.model.id_cliente);
        const storedClientId = this.getEntityId(this.client);

        return String(fincaClientId ?? modelClientId ?? storedClientId ?? '').trim();
    }

    private resolveUserId(selectedFincaValue: unknown = this.model.id_finca): string {
        const selectedFinca = this.resolveSelectedDropdownEntity(this.lists.fincas, selectedFincaValue);
        const fincaUserId = selectedFinca?.id_usuario;
        const modelUserId = this.getEntityId(this.model.id_usuario);
        const storedUserId = this.getEntityId(this.user?.id);

        return String(fincaUserId ?? modelUserId ?? storedUserId ?? '').trim();
    }

    private resolveSelectedDropdownEntity<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity | null {
        return this.resolveSelectedDropdownEntities(dropdownData, selectedValue)[0] ?? null;
    }

    private resolveSelectedDropdownEntities<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity[] {
        const tokens = this.getReactiveValueTokens(selectedValue);

        if (tokens.length === 0) {
            return [];
        }

        return dropdownData.values
            .map((option) => this.getDropdownOptionEntity<TEntity>(option))
            .filter((entity): entity is TEntity => entity !== null)
            .filter((entity) => tokens.includes(this.getEntityId(entity)));
    }

    private getReactiveValueTokens(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getReactiveValueTokens(item));
        }

        if (typeof value === 'object' && value !== null) {
            const entityId = this.getEntityId(value);

            return entityId.length > 0 ? [entityId] : [];
        }

        return String(value ?? '')
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }

    private getDropdownOptionEntity<TEntity extends object>(
        option: { readonly value: TEntity | null; readonly raw?: unknown },
    ): TEntity | null {
        if (this.isRecord(option.raw)) {
            return option.raw as unknown as TEntity;
        }

        if (this.isRecord(option.value)) {
            return option.value as unknown as TEntity;
        }

        return null;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private shouldShowClientSelector(): boolean {
        return this.env.hasClients
            && this.env.canSeeClientes
            && !this.client
            && this.user?.tipo !== 'comunero';
    }

    private getDefaultApplicationDateInput(): Partial<{ defaultValue: Date }> {
        if (!this.env.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION || this.env.appName === 'sirga') {
            return {};
        }

        return { defaultValue: new Date() };
    }

    private getDefaultEndTimeInput(): Partial<{ defaultValue: string }> {
        if (!this.env.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION) {
            return {};
        }

        return { defaultValue: moment().format('HH:mm') };
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return moment(value).format('HH:mm');
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const start = moment(startTime, 'HH:mm', true);
        const end = moment(endTime, 'HH:mm', true);

        if (!start.isValid() || !end.isValid()) {
            console.error('Invalid time format. Expected HH:mm.', {
                startTime,
                endTime,
            });

            return '';
        }

        if (end.isBefore(start)) {
            console.warn('End time is before start time.', {
                startTime,
                endTime,
            });

            return '';
        }

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
